/** @jsx jsx */
import React, { useState, useEffect } from 'react';
import { jsx } from '@emotion/core';
import { useSelector } from 'react-redux';
import {
  Button,
  DatePicker,
  Icon,
  Input,
  Modal,
  Select,
  Switch,
  Table,
  Tooltip,
  message,
} from 'antd';
import moment from 'moment';
import { get } from 'lodash';

import { searchTable, userHasRole } from '../../actions/utils';
import holidays from '../../actions/holidays';
import {
  createBlackoutEntry,
  deleteBlackoutEntry,
  loadBlackoutDates,
  saveBlackoutSettings,
  updateBlackoutEntry,
} from '../../_shared/services/blackoutdates.service';
import styles from './styles';

const { Option } = Select;
const { RangePicker } = DatePicker;

/**
 * Card #254 — Holiday & Blackout Dates (admin screen, roles 0 and 1).
 *
 * Three things live here, and they are stored and saved separately so they can
 * never clobber one another:
 *
 *   1. US holiday presets — toggled on/off. Each one stores only its KEY; the
 *      actual date is computed per year by app/src/actions/holidays.js, so the
 *      floating holidays (Thanksgiving = 4th Thursday of November, Memorial Day
 *      = last Monday of May, ...) stay correct forever with no re-entry.
 *   2. A weekday blocklist — global and SUBTRACTIVE. It only ever removes days
 *      from what the per-ZIP delivery routes already allow; it can never add one.
 *   3. Custom entries — one-time dates, consecutive date ranges, and
 *      every-year-on-this-date repeats. Full create / edit / delete.
 *
 * Everything is a bare YYYY-MM-DD calendar day end to end. No instants, no UTC.
 *
 * SCOPE: each client is its own deployment with its own database, so this
 * calendar applies to every ZIP, route and customer in THIS deployment only.
 * No ZIP-code configuration is involved anywhere.
 */

const TYPE_LABELS = {
  single: 'One-time date',
  range: 'Date range',
  annual: 'Every year',
};

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const PREVIEW_DAYS = 120;

const toMoment = (str) => (holidays.isDateStr(str) ? moment(str, 'YYYY-MM-DD') : null);
const toDay = (m) => (m && typeof m.format === 'function' ? m.format('YYYY-MM-DD') : null);
const pretty = (str) => (holidays.isDateStr(str) ? moment(str, 'YYYY-MM-DD').format('ddd, MMM D, YYYY') : '—');

const EMPTY_DRAFT = () => ({ type: 'single', label: '', date: null, startDate: null, endDate: null, month: null, day: null });

const draftFromEntry = (entry) => {
  if (!entry) { return EMPTY_DRAFT(); }
  return {
    ...EMPTY_DRAFT(),
    ...entry,
    label: get(entry, 'label', '') || '',
  };
};

// The draft carries every type's fields; only the ones the chosen type uses are
// sent, so switching type in the modal cannot smuggle a stale date through.
const entryFromDraft = (draft) => {
  const base = { id: draft.id, type: draft.type, label: (draft.label || '').trim() };
  if (draft.type === 'single') { return { ...base, date: draft.date }; }
  if (draft.type === 'range') { return { ...base, startDate: draft.startDate, endDate: draft.endDate }; }
  return { ...base, month: Number(draft.month), day: Number(draft.day) };
};

const describeEntryDates = (entry) => {
  if (entry.type === 'single') { return pretty(entry.date); }
  if (entry.type === 'range') {
    const days = holidays.listBlackoutDatesInRange({ entries: [entry] }, entry.startDate, entry.endDate).length;
    return `${pretty(entry.startDate)} → ${pretty(entry.endDate)} (${days} day${days === 1 ? '' : 's'})`;
  }
  const month = MONTH_NAMES[Number(entry.month) - 1] || '?';
  return `${month} ${entry.day}, every year`;
};

const EntryModal = ({ visible, entry, saving, onCancel, onSave }) => {
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      setDraft(draftFromEntry(entry));
      setError('');
    }
  }, [visible, entry]);

  const set = (patch) => setDraft(d => ({ ...d, ...patch }));

  const submit = () => {
    const candidate = entryFromDraft(draft);
    const err = holidays.entryError(candidate);
    if (err) { return setError(err); }
    setError('');
    onSave(candidate);
  };

  return (
    <Modal
      title={get(entry, 'id') ? 'Edit Blackout Date' : 'Add Blackout Date'}
      visible={visible}
      onCancel={onCancel}
      onOk={submit}
      okText={get(entry, 'id') ? 'Save' : 'Add'}
      confirmLoading={saving}
      destroyOnClose
    >
      <div css={styles.modalField}>
        <label css={styles.fieldLabel}>Type</label>
        <Select value={draft.type} onChange={v => set({ type: v })} style={{ width: '100%' }}>
          <Option value={'single'}>One-time date (e.g. a one-off closure)</Option>
          <Option value={'range'}>Date range (consecutive days)</Option>
          <Option value={'annual'}>Every year on this date</Option>
        </Select>
      </div>

      <div css={styles.modalField}>
        <label css={styles.fieldLabel}>Label</label>
        <Input
          value={draft.label}
          placeholder={'Christmas Eve'}
          maxLength={120}
          onChange={e => set({ label: e.target.value })}
        />
        <div css={styles.fieldHint}>Shown to admins in the override prompt. Optional.</div>
      </div>

      {draft.type === 'single' && (
        <div css={styles.modalField}>
          <label css={styles.fieldLabel}>Date</label>
          <DatePicker
            style={{ width: '100%' }}
            value={toMoment(draft.date)}
            onChange={m => set({ date: toDay(m) })}
          />
        </div>
      )}

      {draft.type === 'range' && (
        <div css={styles.modalField}>
          <label css={styles.fieldLabel}>First and last day (both blocked)</label>
          <RangePicker
            style={{ width: '100%' }}
            value={[toMoment(draft.startDate), toMoment(draft.endDate)]}
            onChange={ms => set({ startDate: toDay(get(ms, '0')), endDate: toDay(get(ms, '1')) })}
          />
        </div>
      )}

      {draft.type === 'annual' && (
        <div css={styles.modalField}>
          <label css={styles.fieldLabel}>Month and day</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <Select
              value={draft.month || undefined}
              placeholder={'Month'}
              style={{ flex: 2 }}
              onChange={v => set({ month: v })}
            >
              {MONTH_NAMES.map((name, i) => <Option key={name} value={i + 1}>{name}</Option>)}
            </Select>
            <Select
              value={draft.day || undefined}
              placeholder={'Day'}
              style={{ flex: 1 }}
              onChange={v => set({ day: v })}
            >
              {/* 2024 is a leap year, so Feb 29 is offerable; it simply never
                  matches in a common year. */}
              {Array.from({ length: holidays.daysInMonth(2024, draft.month || 1) }, (_v, i) => i + 1)
                .map(d => <Option key={d} value={d}>{d}</Option>)}
            </Select>
          </div>
          <div css={styles.fieldHint}>
            Repeats on this calendar date every year. For holidays that move — Thanksgiving,
            Memorial Day, Labor Day — use the US Holidays toggles above instead, which
            recompute the correct day each year.
          </div>
        </div>
      )}

      {!!error && <div css={styles.modalError}>{error}</div>}
    </Modal>
  );
};

export const BlackoutDates = () => {
  const user = useSelector(state => get(state, 'user'));
  const isAdmin = userHasRole([0, 1], { user });

  const [doc, setDoc] = useState({ presets: [], weekdays: [], entries: [] });
  const [loading, setLoading] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const [modalEntry, setModalEntry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Draft copy of the toggles, so a mis-click can be undone with Cancel instead
  // of firing a save per switch.
  const [presets, setPresets] = useState([]);
  const [weekdays, setWeekdays] = useState([]);

  const applyDoc = (next) => {
    const cfg = holidays.normalizeConfig(next);
    setDoc(cfg);
    setPresets(cfg.presets);
    setWeekdays(cfg.weekdays);
    return cfg;
  };

  const fetchDoc = async () => {
    setLoading(true);
    try {
      // loadBlackoutDates also refreshes the window cache the order form reads,
      // so an edit made here is live for the rest of the session.
      applyDoc(await loadBlackoutDates());
    } catch (err) {
      console.error(err);
      message.error('Failed to load blackout dates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAdmin) {
    return (
      <div css={styles.wrapper}>
        <div css={styles.titleBlock}>
          <h2 css={styles.title}>Holiday &amp; Blackout Dates</h2>
          <p css={styles.subtitle}>You do not have permission to view this screen.</p>
        </div>
      </div>
    );
  }

  const settingsDirty = presets.join(',') !== doc.presets.join(',')
    || weekdays.join(',') !== doc.weekdays.join(',');

  const togglePreset = (key, on) => {
    setPresets(p => (on ? [...p, key] : p.filter(k => k !== key)));
  };

  const toggleWeekday = (dow) => {
    setWeekdays(w => (w.indexOf(dow) > -1 ? w.filter(d => d !== dow) : [...w, dow].sort()));
  };

  const onSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await saveBlackoutSettings({ presets, weekdays });
      // Re-read through loadBlackoutDates so the screen and the window cache the
      // order form reads are refreshed from one source, not two.
      applyDoc(await loadBlackoutDates());
      message.success('Holiday and weekday settings saved');
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.error', 'Failed to save settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const onSaveEntry = async (entry) => {
    setSavingEntry(true);
    try {
      if (entry.id) { await updateBlackoutEntry(entry); } else { await createBlackoutEntry(entry); }
      applyDoc(await loadBlackoutDates());
      setModalOpen(false);
      setModalEntry(null);
      message.success(entry.id ? 'Blackout date updated' : 'Blackout date added');
    } catch (err) {
      console.error(err);
      message.error(get(err, 'response.data.error', 'Failed to save blackout date'));
    } finally {
      setSavingEntry(false);
    }
  };

  const onDeleteEntry = (entry) => {
    Modal.confirm({
      title: 'Delete this blackout date?',
      content: `${entry.label || TYPE_LABELS[entry.type]} — ${describeEntryDates(entry)}. Existing scheduled orders are not affected.`,
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteBlackoutEntry(entry);
          applyDoc(await loadBlackoutDates());
          message.success('Blackout date deleted');
        } catch (err) {
          console.error(err);
          message.error(get(err, 'response.data.error', 'Failed to delete blackout date'));
        }
      },
    });
  };

  const thisYear = moment().year();
  const nextYear = thisYear + 1;

  const columns = [
    {
      // Card #277 — an unlabelled entry shows its type name in grey, so that is what the
      // search has to match; likewise Type shows the label, not the stored code.
      title: 'Label',
      dataIndex: 'label',
      searchValue: r => get(r, 'label') || TYPE_LABELS[get(r, 'type')],
      render: (v, r) => v || <span style={{ color: '#bbb' }}>{TYPE_LABELS[r.type]}</span>,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: 150,
      searchValue: r => TYPE_LABELS[get(r, 'type')] || get(r, 'type'),
      render: v => <span css={styles.kindPill}>{TYPE_LABELS[v] || v}</span>,
    },
    {
      title: 'Date(s) blocked',
      key: 'dates',
      searchValue: r => describeEntryDates(r),
      render: (_t, r) => describeEntryDates(r),
    },
    {
      title: 'Actions',
      key: 'rowActions',
      width: 100,
      render: (_t, r) => (
        <div>
          <Icon
            type={'edit'}
            css={styles.iconBtn}
            onClick={() => { setModalEntry(r); setModalOpen(true); }}
          />
          <Icon type={'delete'} css={styles.iconBtnDanger} onClick={() => onDeleteEntry(r)} />
        </div>
      ),
    },
  ];

  // The preview is computed from the SAVED document, not the unsaved toggles, so
  // it always shows what scheduling is actually enforcing right now.
  const todayStr = moment().format('YYYY-MM-DD');
  const preview = holidays.listBlackoutDatesInRange(
    doc,
    todayStr,
    holidays.addDays(todayStr, PREVIEW_DAYS),
  );

  return (
    <div css={styles.wrapper}>
      <div css={styles.titleBlock}>
        <h2 css={styles.title}>Holiday &amp; Blackout Dates</h2>
        <p css={styles.subtitle}>
          Days listed here are not offered anywhere in scheduling. This applies to every ZIP code,
          route and customer — no ZIP-code configuration is needed. Orders that are already
          scheduled are never changed.
        </p>
      </div>

      <div css={styles.card}>
        <div css={styles.cardHead}>
          <div>
            <h3 css={styles.cardTitle}>US Holidays</h3>
            <p css={styles.cardHint}>
              Turn a holiday on and it is blocked every year, automatically. Holidays that move —
              Thanksgiving, Memorial Day, Labor Day, MLK Day, Presidents' Day, Columbus Day — are
              recalculated for each year, so there is nothing to re-enter.
            </p>
          </div>
        </div>

        <div css={styles.presetGrid}>
          {holidays.US_HOLIDAYS.map(h => (
            <div key={h.key} css={styles.presetRow}>
              <Switch
                size={'small'}
                checked={presets.indexOf(h.key) > -1}
                onChange={on => togglePreset(h.key, on)}
              />
              <div css={styles.presetLabel}>
                <span css={styles.presetName}>{h.label}</span>
                <span css={styles.presetDates}>
                  {thisYear}: {pretty(holidays.holidayDateForYear(h.key, thisYear))}
                  {'  ·  '}
                  {nextYear}: {pretty(holidays.holidayDateForYear(h.key, nextYear))}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div css={styles.card}>
        <div css={styles.cardHead}>
          <div>
            <h3 css={styles.cardTitle}>Blocked Days of the Week</h3>
            <p css={styles.cardHint}>
              Optional. A blocked weekday is removed from delivery availability everywhere, every
              week. This only ever takes days away from what the delivery routes already allow — it
              never makes a day available that the routes do not already cover.
            </p>
          </div>
        </div>

        <div css={styles.weekdayRow}>
          {holidays.WEEKDAY_LABELS.map((name, dow) => {
            const on = weekdays.indexOf(dow) > -1;
            return (
              <Button
                key={name}
                css={on ? styles.weekdayOn : undefined}
                onClick={() => toggleWeekday(dow)}
              >
                {name}
              </Button>
            );
          })}
        </div>

        <div css={styles.saveRow}>
          {settingsDirty && <span css={styles.dirty}>You have unsaved holiday / weekday changes.</span>}
          <Button
            disabled={!settingsDirty || savingSettings}
            onClick={() => { setPresets(doc.presets); setWeekdays(doc.weekdays); }}
          >
            Cancel
          </Button>
          <Button
            type={'primary'}
            loading={savingSettings}
            disabled={!settingsDirty}
            onClick={onSaveSettings}
          >
            Save Holidays &amp; Weekdays
          </Button>
        </div>
      </div>

      <div css={styles.card}>
        <div css={styles.cardHead}>
          <div>
            <h3 css={styles.cardTitle}>Custom Blackout Dates</h3>
            <p css={styles.cardHint}>
              One-time closures, consecutive date ranges, and your own every-year dates. Saved
              immediately when you add, edit or delete.
            </p>
          </div>
          <div>
            <Input.Search
              placeholder="Search all columns"
              value={search}
              allowClear
              style={{ width: 240, marginRight: 8 }}
              onChange={e => setSearch(e.target.value)}
            />
            <Tooltip title={'Reload from the server'}>
              <Button icon={'reload'} loading={loading} onClick={fetchDoc} style={{ marginRight: 8 }} />
            </Tooltip>
            <Button
              type={'primary'}
              icon={'plus'}
              onClick={() => { setModalEntry(null); setModalOpen(true); }}
            >
              Add Blackout Date
            </Button>
          </div>
        </div>

        <Table
          rowKey={'id'}
          size={'middle'}
          loading={loading}
          dataSource={searchTable(columns, doc.entries, search)}
          columns={columns}
          pagination={false}
          locale={{ emptyText: 'No custom blackout dates yet.' }}
        />
      </div>

      <div css={styles.card}>
        <div css={styles.cardHead}>
          <div>
            <h3 css={styles.cardTitle}>Next {PREVIEW_DAYS} Days — What Is Blocked</h3>
            <p css={styles.cardHint}>
              Computed from the saved calendar above. These are the days no one can select as a
              delivery, pickup or drop-off date.
            </p>
          </div>
        </div>

        {preview.length === 0 ? (
          <div css={styles.empty}>Nothing is blocked in the next {PREVIEW_DAYS} days.</div>
        ) : (
          <div css={styles.previewList}>
            {preview.map(p => (
              <div key={p.date} css={styles.previewItem}>
                {pretty(p.date)} <span css={styles.previewWhy}>— {p.label}</span>
              </div>
            ))}
          </div>
        )}

        <div css={styles.footNote}>
          Super admins and admins can still override a blocked day when creating an order; everyone
          else cannot select one at all.
        </div>
      </div>

      <EntryModal
        visible={modalOpen}
        entry={modalEntry}
        saving={savingEntry}
        onCancel={() => { setModalOpen(false); setModalEntry(null); }}
        onSave={onSaveEntry}
      />
    </div>
  );
};

export default BlackoutDates;
